import * as React from 'react';
import * as c from 'models/constants';
import { translate } from 'react-i18next';
import { reduxForm } from 'redux-form';
import { browserHistory, RouteComponentProps } from 'react-router';
import { Dispatch } from 'redux';
const { connect } = require('react-redux');
import { IStore } from 'redux/IStore';
import { Button, ErrorSummary, LoadingOverlayWrapper } from 'components';
import {
	getBookingTemplatesByOffice,
	getBookingTemplatesByServiceGroup,
	removeBookingTemplates,
	resetBookingTemplatesData,
} from 'redux/modules/shop/bookingtemplates';
import { InjectedFormProps, ITranslateProps } from 'models/common';
import { getShopCard } from 'redux/modules/shop/shopcard';
import { getServiceCategoriesDomainValues } from 'redux/modules/domainvalues';
import { deleteServiceGroups, getOfficeServiceGroups } from 'redux/modules/shop/serviceGroups';
import { IServiceForm, IServiceFormValues, IServicesList } from 'models/shop/shopBookingTemplates';
import { BasicInformation } from './BasicInformation';
import renderIcon from 'helpers/renderIcon';
import { ServiceSettings } from './ServiceSettings';
import { getGuidFromString, readURLSearchParams, uniq } from 'helpers/common';
import { v4 as uuidv4 } from 'uuid';
import {
	calculateOnScreenServiceVariations,
	hasSubservices,
	isNormalService,
	splitAndCombineVariations,
} from '../helpers';
import { addServiceGroup, updateServiceGroup } from 'redux/modules/shop/serviceGroups/helpers';
import { IServiceGroup } from 'models/shop/serviceGroups';
import {
	IApiService,
	IVariantGroups,
	saveMultipleGroupedBookingTemplate,
} from 'redux/modules/shop/bookingtemplates/helpers';
import {
	updatedBatchAddBookingTimeTablesToServices,
	getResourceTimetables,
} from 'redux/modules/shop/bookingtimetables/helpers';
import {
	deleteBookingTemplateVariants,
	fetchBookingTemplateVariants,
	getBundlesForChildren,
	sequentialWithBreak,
} from 'redux/modules/shop/helpers';
import { actions as notifActions } from 'redux-notifications';
import { closeModal, showModal } from 'redux/modules/modal';
import { IResourceTimeTables } from 'models/shop/bookingtimetables';
import { scrollErrorIntoView } from 'helpers/validationattributes';
import moment = require('moment');

interface IParams {
	shopId: string;
	officeId: string;

	// Used in edit mode
	serviceGroupId: string;
	variantParentId: string;
}

interface IRouterParams {
	mode: 'add' | 'edit';
}

type IRouterProps = RouteComponentProps<IParams, IRouterParams>;

interface IState {
	editHasFetchedResources: boolean;
	timesResetServiceInBundle: number; // Counter for times failed, used in ServiceSettings to reset hasSubservices state
}

interface IProps
	extends InjectedFormProps<IServiceFormValues>,
		IRouterProps,
		ITranslateProps,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps> {
	route: any;
}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ShopServicesSingleAddEditForm extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props);

		this.state = {
			editHasFetchedResources: false,
			timesResetServiceInBundle: 0,
		};

		this.onSubmit = this.onSubmit.bind(this);
		this.handleOpenDeleteModal = this.handleOpenDeleteModal.bind(this);
		this.deleteServices = this.deleteServices.bind(this);
		this.generateBundlesErrorMessage = this.generateBundlesErrorMessage.bind(this);
		this.submitButtonDisabledCheck = this.submitButtonDisabledCheck.bind(this);
	}

	public componentDidMount() {
		const { params, route } = this.props;

		this.props.getShopAndServices(params.shopId, params.officeId, params.serviceGroupId, route.mode);
		this.props.getOfficeServiceGroups(params.officeId);

		if (!this.props.serviceCategories?.length) {
			this.props.getServiceCategoriesDomainValues();
		}
	}

	public componentDidUpdate(prevProps: IProps) {
		const { params, bookingTemplates, route } = this.props;

		if (
			prevProps.params.shopId !== params.shopId ||
			(route.mode === 'edit' && !bookingTemplates.services?.length && !bookingTemplates.isFetching)
		) {
			this.props.getShopAndServices(params.shopId, params.officeId, params.serviceGroupId, route.mode);
		}
	}

	public componentWillUnmount() {}

	private redirectToEditServices = () => {
		const { shopId, officeId } = this.props.params;

		this.props.reset();
		browserHistory.replace(`/shop/${shopId}/${officeId}/edit/services/`);
	};

	private onSubmit = (): Promise<any> => {
		const { currentFormValues, serviceGroups, t } = this.props;
		const { shopId, officeId } = this.props.params;
		const serviceCategory = currentFormValues.values.info.serviceCategory;
		const serviceGroupId = currentFormValues.values.info.serviceGroup || readURLSearchParams(c.SERVICE_GROUP);
		const orderNumber = serviceGroups.filter((sg) => sg.parentId === serviceGroupId).length + 1;
		const dateLimit: [moment.Moment, moment.Moment] = [moment().add(-1, 'days'), moment().add(730, 'days')]; // Limit to 2 years for now, properly we would need to fetch the last date and use that as the end limit
		const parentServiceGroup = serviceGroups.find((sg) => sg.id === serviceGroupId);

		if (this.props.route.mode === 'add') {
			const serviceGroupValues = {
				order: orderNumber,
				parentId: serviceGroupId,
				serviceCategoryId: serviceCategory,
				officeId,
				value: currentFormValues.values.info.serviceName,
				type: null,
				id: '',
				color: parentServiceGroup?.color || '#EA3546', // Use default color if parent doesn't have one set
			};

			const newServiceValues = (serviceValues, res: { id: string }, _orderIndex: number) => {
				return {
					shopId,
					officeId,
					duration: Number(serviceValues.duration) * 60, // in seconds
					price: Number(serviceValues.price),
					categoryKey: serviceCategory,
					resources: serviceValues.performers
						? Object.keys(serviceValues.performers).filter((perId) => serviceValues.performers[perId] === true)
						: [],
					serviceGroupId: res.id,
					sku: uuidv4(),
					description: currentFormValues.values.info.serviceDescription,
					order: _orderIndex, // Take current index of the services array (would need a rework elsewhere)
				};
			};

			return this.props
				.addServiceGroup(() => this.props.state, { ...serviceGroupValues })
				.then((res: { id: string }) => {
					// Filter out any deleted subservices
					const allServices = Object.values(currentFormValues.values.services).filter(
						(subservice) => !subservice.deleted,
					);

					if (allServices.length) {
						const kvpairs: IVariantGroups[] = [];
						allServices.forEach((ss, idx) => {
							const servicesToDispatch: IApiService[] = [];

							// Combine variations with same price and duration to not make unneccessary duplicates
							const allVariations = [];

							Object.keys(ss.serviceVariations).forEach((variationName) => {
								const variationValues = ss.serviceVariations[variationName];

								if (isNormalService(ss, variationName) === true) {
									servicesToDispatch.push({
										...newServiceValues(variationValues, res, idx),
										name: hasSubservices(currentFormValues.values)
											? ss.info.subServiceName
											: currentFormValues.values.info.serviceName,
										published: ss.serviceSettings.bookability,
									});
								}

								if (isNormalService(ss, variationName) === false) {
									if (variationValues) {
										const performerId = Object.keys(variationValues.performers)[0];

										if (variationValues.performers[performerId] === true) {
											allVariations.push(variationValues);
										}
									}
								}
							});

							const combinedVariations = splitAndCombineVariations([...allVariations]) || [];

							if (combinedVariations.length > 0) {
								combinedVariations.map((cv) => {
									servicesToDispatch.push({
										...newServiceValues(cv, res, idx),
										name: hasSubservices(currentFormValues.values)
											? ss.info.subServiceName
											: currentFormValues.values.info.serviceName,
										published: ss.serviceSettings.bookability,
									});
								});
							}

							if (servicesToDispatch.length) {
								kvpairs.push({ item1: null, item2: servicesToDispatch }); // Item 1 is variantParentId, item2 are variation services
							}
						});

						if (kvpairs.length) {
							const allResourceIds = [];

							// Fill the allResourceIds array with resourceIds that are inside the new/updated services
							kvpairs.map(({ item2 }) => {
								item2.map((variation: IApiService) => {
									variation.resources.map((resId) => {
										if (!allResourceIds.includes(resId)) {
											allResourceIds.push(resId);
										}
									});
								});
							});

							return Promise.all(
								allResourceIds.map((resourceId) =>
									this.props
										.getResourceTimeTables(() => this.props.state, resourceId, dateLimit)
										.then((timeTables) => ({
											resourceId,
											timeTables,
										})),
								),
							).then((allTimeTablesResourcesTuples) => {
								return this.props
									.saveMultipleGroupedBookingTemplate(() => this.props.state, shopId, kvpairs)
									.then((res: string[]) => {
										return this.props
											.getBookingTemplatesByOffice(this.props.params.officeId, true)
											.then((resS) => {
												const allServices: IServicesList[] = resS.payload.services;
												const newServices = [];

												res.forEach((value) => {
													const returnedVariantParentId = getGuidFromString(value);
													if (returnedVariantParentId) {
														[...allServices]
															.filter((ser) => ser.variantParentId === returnedVariantParentId)
															.map((pSer) => {
																newServices.push(pSer);
															});
													}
												});

												return this.props
													.updatedBatchAddBookingTimeTablesToServices(
														() => this.props.state,
														allServices,
														newServices,
														allTimeTablesResourcesTuples,
													)
													.then(() => {
														this.props.showNotification(t('Shop_Services_ServicesSavedSuccessfully'), 'success');
														this.redirectToEditServices();
													})
													.catch((err) => {
														this.props.showNotification(
															`${currentFormValues.values.info.serviceName} - ${err}`,
															'warning',
														);
													});
											})
											.catch((err) => {
												this.props.showNotification(`${currentFormValues.values.info.serviceName} - ${err}`, 'warning');
											});
									})
									.catch((err) => {
										const generateError = () => {
											if (err?.error && Array.isArray(err.error)) {
												if (err.error[1]) {
													const extractedSKU = getGuidFromString(err.error[1]);
													if (extractedSKU) {
														const stringSplitArr = err.error[1].split(extractedSKU);
														return (
															stringSplitArr[0] +
															this.props.bookingTemplates.services?.find((x) => x.sku === extractedSKU).name +
															stringSplitArr[1]
														);
													}
												}
											}
											return '';
										};
										this.props.showNotification(generateError(), 'warning');
									});
							});
						}
					}
				});
		} else if (this.props.route.mode === 'edit') {
			const serviceGroupBeforeEditing = this.props.serviceGroups.find(
				(sg) => sg.id === this.props.params.serviceGroupId,
			);

			const updateServiceGroup = (): Promise<any> => {
				return new Promise((resolve, reject) => {
					if (JSON.stringify(currentFormValues.values.info) !== JSON.stringify(currentFormValues.initial.info)) {
						const serviceGroupToUpdate: IServiceGroup = {
							...serviceGroupBeforeEditing,
							value: currentFormValues.values.info?.serviceName,
							parentId: currentFormValues.values.info?.serviceGroup,
							serviceCategoryId: currentFormValues.values.info?.serviceCategory,
							color: parentServiceGroup?.color || '#EA3546', // Use default color if parent doesn't have one set
						};

						this.props
							.updateServiceGroup(() => this.props.state, serviceGroupToUpdate.id, serviceGroupToUpdate)
							.then(() => {
								this.props.showNotification(t('Shop_Services_ServicesSavedSuccessfully'), 'success');
								resolve(true);
							})
							.catch((err) => {
								reject(err);
							});
					} else {
						resolve(true); // Nothing to update
					}
				});
			};

			const updateAddBookingTemplates = (): Promise<any> => {
				return new Promise((resolve, reject) => {
					if (
						JSON.stringify(currentFormValues.values.services) !== JSON.stringify(currentFormValues.initial.services) ||
						JSON.stringify(currentFormValues.values.info) !== JSON.stringify(currentFormValues.initial.info)
					) {
						const servicesToDispatch: IApiService[] = [];

						// Filter services that have the "deleted" flag
						const notDeletedServices = Object.values(currentFormValues.values.services).filter((ser) => !ser.deleted);

						// Go through services
						notDeletedServices.forEach((ser, serviceIndex) => {
							const initialCurrentServiceValues = Object.values(currentFormValues.initial.services).find(
								(initSer) => initSer.info.variantParentId === ser.info.variantParentId,
							);

							const anyOrder = this.props.bookingTemplates.services?.find(
								(bt) =>
									Object.keys(ser.serviceVariations).findIndex(
										(variationName) => bt.id === ser.serviceVariations[variationName].bookingTemplateId,
									) != -1,
							)?.order;

							const normalServiceOrder = this.props.bookingTemplates.services?.find(
								(bt) => bt.id === ser.serviceVariations?.normalService?.bookingTemplateId,
							)?.order;

							// Check if anything in entire service has changed
							// Also check if service description or service group name has changed (only if one service in group)
							if (
								!initialCurrentServiceValues ||
								(initialCurrentServiceValues && JSON.stringify(initialCurrentServiceValues) !== JSON.stringify(ser)) ||
								currentFormValues.initial.info.serviceDescription !==
									currentFormValues.values.info.serviceDescription ||
								(notDeletedServices.length === 1 &&
									currentFormValues.initial.info.serviceName !== currentFormValues.values.info.serviceName) ||
								(anyOrder && anyOrder != serviceIndex)
							) {
								const newVariantParentId = uuidv4(); // New variant parent id used for each new service

								const generateNewServiceValues = (variation, variantParentId?: string) => {
									return {
										shopId,
										officeId,
										duration: Number(variation.duration) * 60, // must be in seconds
										price: Number(variation.price),
										categoryKey: currentFormValues.values.info.serviceCategory,
										resources: variation.performers
											? Object.keys(variation.performers).filter(
													(performedId) => variation.performers[performedId] === true,
											  )
											: [],
										serviceGroupId: this.props.params.serviceGroupId,
										sku: uuidv4(),
										description: currentFormValues.values.info.serviceDescription,
										variantParentId: variantParentId ? variantParentId : newVariantParentId, // When new variation on existing service else new variantParentId if entire new service
										id: uuidv4(), // new bookingTemplateId
										order: serviceIndex, // Take current index of the services array (would need a rework elsewhere)
									};
								};

								const generateUpdatedServiceValues = (service, variation) => {
									const ogBT = this.props.bookingTemplates.services?.find(
										(bt) => bt.id === variation.bookingTemplateId,
									);

									return {
										shopId,
										officeId,
										duration: Number(variation.duration) * 60, // must be in seconds
										price: Number(variation.price),
										categoryKey: currentFormValues.values.info.serviceCategory,
										resources: variation.performers
											? Object.keys(variation.performers).filter(
													(performedId) => variation.performers[performedId] === true,
											  )
											: [],
										serviceGroupId: ogBT.serviceGroupId,
										sku: ogBT.sku,
										description: currentFormValues.values.info.serviceDescription,
										variantParentId: service.info.variantParentId,
										id: variation.bookingTemplateId,
										name:
											hasSubservices(currentFormValues.values) === false
												? currentFormValues.values.info.serviceName
												: service.info.subServiceName, // update name depending if there are subservices or not anymore
										published: service.serviceSettings.bookability,
										order: serviceIndex, // Take serviceGroup order number (we currently order like this on shop order, not optimal)
									};
								};

								// Check if the variantParentId is set for the service, if it does then the service already exists
								if (ser.info.variantParentId) {
									let originalNormalService: { bookingTemplateId?: string; price?; duration?; performers? } = {};
									let newNormalService: { bookingTemplateId?: string; price?; duration?; performers? } = {};

									const allOriginalVariations = [];
									const allNewVariations = [];

									// Go through original service variations
									for (const key in initialCurrentServiceValues.serviceVariations) {
										const variationValues = initialCurrentServiceValues.serviceVariations[key];

										// If initial normal service
										if (isNormalService(initialCurrentServiceValues, key) === true) {
											originalNormalService = { ...initialCurrentServiceValues.serviceVariations[key] };
										}

										// If initial variation service
										if (isNormalService(initialCurrentServiceValues, key) === false) {
											if (variationValues) {
												const performerId = Object.keys(variationValues.performers)[0];

												if (variationValues.performers[performerId] === true) {
													allOriginalVariations.push(variationValues);
												}
											}
										}
									}

									// Go through new service variations
									for (const key in ser.serviceVariations) {
										const variationValues = ser.serviceVariations[key];

										// Also check if subservice, change subserviceName of each variation(with updateBookingTemplate?)

										// If normal service
										if (isNormalService(ser, key) === true) {
											newNormalService = { ...ser.serviceVariations[key] };
										}

										// If variation service
										if (isNormalService(ser, key) === false) {
											if (variationValues) {
												const performerId = Object.keys(variationValues.performers)[0];

												if (variationValues.performers[performerId] === true) {
													allNewVariations.push(variationValues);
												}
											}
										}
									}

									const combinedOriginalVariations =
										splitAndCombineVariations([...allOriginalVariations], this.props.route.mode) || [];
									const combinedNewVariations =
										splitAndCombineVariations([...allNewVariations], this.props.route.mode) || [];

									// Compare if old normal service is not the same as new normal service
									// Also compare if service group description changed or service group name changed
									if (
										JSON.stringify(originalNormalService) !== JSON.stringify(newNormalService) ||
										(Object.keys(newNormalService).length > 0 &&
											currentFormValues.initial.info.serviceDescription !==
												currentFormValues.values.info.serviceDescription) ||
										(Object.keys(newNormalService).length > 0 &&
											notDeletedServices.length === 1 &&
											currentFormValues.initial.info.serviceName !== currentFormValues.values.info.serviceName) ||
										(Object.keys(newNormalService).length > 0 &&
											initialCurrentServiceValues.serviceSettings.bookability !== ser.serviceSettings.bookability) ||
										(Object.keys(newNormalService).length > 0 &&
											initialCurrentServiceValues &&
											initialCurrentServiceValues.info.subServiceName !== ser.info.subServiceName) ||
										(normalServiceOrder && normalServiceOrder != serviceIndex)
									) {
										// Update normal service if old normal service had values and newNormalService has values
										if (Object.keys(originalNormalService)?.length > 0 && Object.keys(newNormalService)?.length > 0) {
											const updatedValues = generateUpdatedServiceValues(ser, newNormalService);
											servicesToDispatch.push(updatedValues);
										} else if (
											Object.keys(originalNormalService)?.length === 0 &&
											Object.keys(newNormalService)?.length > 0 &&
											combinedOriginalVariations?.length > 0
										) {
											// If variation service before and now only normal service
											// Set the booking template id of the first to be deleted variation
											newNormalService.bookingTemplateId = combinedOriginalVariations[0]?.bookingTemplateId;
											const updatedValues = generateUpdatedServiceValues(ser, newNormalService);
											servicesToDispatch.push(updatedValues);
										}
									}

									// If booking template exists already then check what changed and generate new values
									// Must always send all new variations if updating (BE deletes existing variations that are not sent in update call)
									if (combinedNewVariations.length > 0) {
										combinedNewVariations.forEach((updatedVariation) => {
											// Compare with initial variations
											const initialVariation = combinedOriginalVariations.find(
												(cov) => cov.bookingTemplateId === updatedVariation.bookingTemplateId,
											);

											// If length > 1 it means that an existing variation must now be split (create new service for it)
											const newVariations = combinedNewVariations.filter(
												(cov) => cov.bookingTemplateId === updatedVariation.bookingTemplateId,
											);

											const ogBT =
												initialVariation &&
												this.props.bookingTemplates.services?.find(
													(bt) => bt.id === initialVariation.bookingTemplateId,
												);

											// If not the same then update variation
											// Also compare if service group description changed or service group name changed
											if (
												(initialVariation &&
													(JSON.stringify(initialVariation) !== JSON.stringify(updatedVariation) ||
														currentFormValues.initial.info.serviceDescription !==
															currentFormValues.values.info.serviceDescription ||
														(notDeletedServices.length === 1 &&
															currentFormValues.initial.info.serviceName !==
																currentFormValues.values.info.serviceName))) ||
												(ogBT && ogBT.order != serviceIndex)
											) {
												// Check if multiple variations with same bookingTemplateId (if a previous variation is now split into different values)
												// Causes duplicates which we filter out at the end
												if (newVariations.length > 1) {
													newVariations.map((nv, idx) => {
														if (idx === 0) {
															const updatedFirstVariation = {
																...nv,
																bookingTemplateId: initialVariation.bookingTemplateId,
															};
															const updatedValues = generateUpdatedServiceValues(ser, updatedFirstVariation);
															servicesToDispatch.push(updatedValues);
														} else {
															servicesToDispatch.push({
																...generateNewServiceValues(nv, ser.info.variantParentId),
																name:
																	hasSubservices(currentFormValues.values) === false
																		? currentFormValues.values.info.serviceName
																		: ser.info.subServiceName,
																published: Boolean(ser.serviceSettings.bookability),
															});
														}
													});
												} else if (
													newVariations.length === 1 &&
													updatedVariation?.bookingTemplateId &&
													(initialVariation.price !== updatedVariation.price ||
														initialVariation.duration !== updatedVariation.duration ||
														JSON.stringify(initialVariation.performers) !== JSON.stringify(updatedVariation.performers))
												) {
													const updatedValues = generateUpdatedServiceValues(ser, updatedVariation);
													servicesToDispatch.push(updatedValues);
												}
											}
											// Push non updated booking templates
											// Also compare if service group description changed or service group name changed
											if (
												updatedVariation?.bookingTemplateId &&
												((initialVariation && JSON.stringify(initialVariation) === JSON.stringify(updatedVariation)) ||
													currentFormValues.initial.info.serviceDescription !==
														currentFormValues.values.info.serviceDescription ||
													(notDeletedServices.length === 1 &&
														currentFormValues.initial.info.serviceName !== currentFormValues.values.info.serviceName))
											) {
												const oldValues = generateUpdatedServiceValues(ser, updatedVariation);
												servicesToDispatch.push(oldValues);
											}

											// If variation is not inside initial variation service then add new variation
											if (!initialVariation && !updatedVariation?.bookingTemplateId) {
												servicesToDispatch.push({
													...generateNewServiceValues(updatedVariation, ser.info.variantParentId),
													name:
														hasSubservices(currentFormValues.values) === false
															? currentFormValues.values.info.serviceName
															: ser.info.subServiceName,
													published: Boolean(ser.serviceSettings.bookability),
												});
											}
										});
									}
								} else {
									// Service does not exist yet

									const allVariations = [];

									for (const key in ser.serviceVariations) {
										const variationValues = ser.serviceVariations[key];

										// If normal service
										if (isNormalService(ser, key) === true) {
											servicesToDispatch.push({
												...generateNewServiceValues(variationValues),
												name:
													hasSubservices(currentFormValues.values) === false
														? currentFormValues.values.info.serviceName
														: ser.info.subServiceName,
												published: Boolean(ser.serviceSettings.bookability),
											});
										}

										// If variation service
										if (isNormalService(ser, key) === false) {
											if (variationValues) {
												const performerId = Object.keys(variationValues.performers)[0];

												if (variationValues.performers[performerId] === true) {
													allVariations.push(variationValues);
												}
											}
										}
									}

									const combinedVariations = splitAndCombineVariations([...allVariations]) || [];

									if (combinedVariations.length > 0) {
										combinedVariations.map((cv) => {
											servicesToDispatch.push({
												...generateNewServiceValues(cv),
												name:
													hasSubservices(currentFormValues.values) === false
														? currentFormValues.values.info.serviceName
														: ser.info.subServiceName,
												published: Boolean(ser.serviceSettings.bookability),
											});
										});
									}
								}
							}
						});

						// Dispatch functions
						// Variations that no longer exist are handled by BE (checks what we send in the save call)

						// Filter duplicates
						const filteredServicesToDispatch = servicesToDispatch.filter((e, i) => {
							return (
								servicesToDispatch.findIndex((x) => {
									return (
										JSON.stringify(x.resources) == JSON.stringify(e.resources) &&
										x.variantParentId === e.variantParentId
									);
								}) === i
							);
						});

						const vpidArray: string[] = [];
						filteredServicesToDispatch.forEach((service) => {
							if (!vpidArray.includes(service.variantParentId)) {
								vpidArray.push(service.variantParentId);
							}
						});

						const generateTargetServices = (index: number) => {
							return filteredServicesToDispatch.filter((std) => std.variantParentId === vpidArray[index]);
						};

						// Array of objects containing their variantParentId and service data
						const kvpairs: IVariantGroups[] = [];
						if (vpidArray?.length) {
							vpidArray.forEach((value, idx) => {
								kvpairs.push({ item1: value, item2: generateTargetServices(idx) }); // Item 1 is variantParentId, item2 are variation services
							});
						} else {
							resolve(true); // Nothing to add/update so resolve
						}

						if (kvpairs.length) {
							const allResourceIds = [];

							// Fill the allResourceIds array with resourceIds that are inside the new/updated services
							kvpairs.map(({ item2 }) => {
								item2.map((variation: IApiService) => {
									variation.resources.map((resId) => {
										if (!allResourceIds.includes(resId)) {
											allResourceIds.push(resId);
										}
									});
								});
							});

							// Get time tables before saving
							// Saving deletes all of the existing bookingTemplate's time tables so fetch them before saving
							// A bug appears where the "work time" would get deleted if the resource only had 1 booking template assigned to them
							Promise.all(
								allResourceIds.map((resourceId) =>
									this.props
										.getResourceTimeTables(() => this.props.state, resourceId, dateLimit)
										.then((timeTables) => ({
											resourceId,
											timeTables,
										})),
								),
							).then((allTimeTablesResourcesTuples) => {
								this.props
									.saveMultipleGroupedBookingTemplate(() => this.props.state, shopId, kvpairs)
									.then((res: string[]) => {
										this.props
											.getBookingTemplatesByOffice(this.props.params.officeId, true)
											.then((resS) => {
												const allServices: IServicesList[] = resS.payload.services;
												const newServices = [];

												res.forEach((value) => {
													const returnedVariantParentId = getGuidFromString(value);
													if (returnedVariantParentId) {
														[...allServices]
															.filter((ser) => ser.variantParentId === returnedVariantParentId)
															.map((pSer) => {
																newServices.push(pSer);
															});
													}
												});

												this.props
													.updatedBatchAddBookingTimeTablesToServices(
														() => this.props.state,
														allServices,
														newServices,
														allTimeTablesResourcesTuples,
													)
													.then(() => {
														this.props.showNotification(t('Shop_Services_ServicesSavedSuccessfully'), 'success');

														resolve(true);
													})
													.catch((err) => {
														reject(`${currentFormValues.values.info.serviceName} - ${err}`);
													});
											})
											.catch((err) => {
												reject(`${currentFormValues.values.info.serviceName} - ${err}`);
											});
									})
									.catch((err) => {
										const generateError = () => {
											if (err?.error && Array.isArray(err.error)) {
												if (err.error[1]) {
													const extractedSKU = getGuidFromString(err.error[1]);
													if (extractedSKU) {
														const stringSplitArr = err.error[1].split(extractedSKU);
														return (
															stringSplitArr[0] +
															this.props.bookingTemplates.services?.find((x) => x.sku === extractedSKU).name +
															stringSplitArr[1]
														);
													}
												}
											}
											return '';
										};
										reject(generateError());
									});
							});
						}
					} else {
						resolve(true); // Resolve if nothing changed in services
					}
				});
			};

			// For deleting subservices
			const deleteBookingTemplates = (): Promise<any> => {
				return new Promise((resolve, reject) => {
					const vpidsToDelete: string[] = [];

					if (
						JSON.stringify(currentFormValues.values.services) !== JSON.stringify(currentFormValues.initial.services)
					) {
						// Check if the current form values of the initial existing service has the deleted property and delete it if it does
						Object.keys(currentFormValues.initial.services).forEach((ser, idx) => {
							if (currentFormValues.values.services[ser] && currentFormValues.values.services[ser]?.deleted === true) {
								const variantParentId = Object.values(currentFormValues.initial.services)[idx].info.variantParentId;
								vpidsToDelete.push(variantParentId);
							}
						});
					}

					if (vpidsToDelete.length) {
						const bookingTemplateIdsToCheck: string[] = [];

						// Get variant parent children BT ID's for checking if inside a bundle
						vpidsToDelete.forEach((vpid) => {
							const tempBookingTemplateIds = this.props.bookingTemplates.services
								.filter((ser) => ser.variantParentId === vpid)
								.map((ser2) => ser2.id);
							bookingTemplateIdsToCheck.push(...tempBookingTemplateIds);
						});

						// Check if any of the to be deleted services are inside a bundle
						getBundlesForChildren(() => this.props.state, bookingTemplateIdsToCheck)
							.then((bundles: IServicesList[]) => {
								if (bundles?.length) {
									this.props.change('services', this.resetDeletedServices()); // Remove deleted flag from deleted subservices
									reject(this.generateBundlesErrorMessage(bundles));
								} else {
									return Promise.all(
										vpidsToDelete.map((id) => this.props.deleteBookingTemplateVariants(() => this.props.state, id)),
									)
										.then(() => {
											this.props.showNotification(t('User_ShopCard_Services_Notification_Deleted'), 'success');

											resolve(true);
										})
										.catch((err) => {
											reject(err);
										});
								}
							})
							.catch((err) => {
								reject(err);
							});
					} else {
						resolve(true); // Nothing to delete
					}
				});
			};

			return sequentialWithBreak([
				() => deleteBookingTemplates(),
				() => updateServiceGroup(),
				() => updateAddBookingTemplates(),
			])
				.then(() => {
					this.redirectToEditServices();
				})
				.catch((err) => {
					console.error(err);
					this.props.showNotification(err ? JSON.stringify(err) : '', 'warning', 5000);
				});
		}
	};

	private handleOpenDeleteModal = () => {
		const { t, showModal, params } = this.props;
		const dialogContent = (
			<>
				<h1>{t('BSPortal_Common_ConfirmationDialog_Sure')}</h1>
				<p className="p14">{t('User_ShopCard_Services_Action_ThisActionWillDelete')}</p>
			</>
		);

		showModal('ConfirmationDialog', {
			confirmAction: this.deleteServices,
			value: params.variantParentId,
			yesButtonText: t('User_ShopCard_Services_DeleteService'),
			noButtonText: t('BSPortal_Common_Cancel'),
			content: dialogContent,
		});
	};

	private deleteServices = (confirm: boolean) => {
		const { closeModal, deleteServiceGroups, servicesToEdit, t, showNotification, currentFormValues } = this.props;
		const name = currentFormValues.initial.info.serviceName;
		if (confirm && servicesToEdit.length) {
			const serviceGroupIds = uniq(servicesToEdit.map((s) => s.serviceGroupId).filter(Boolean));
			const serviceIdsToDelete = servicesToEdit.map((s) => s.id);

			getBundlesForChildren(() => this.props.state, serviceIdsToDelete)
				.then((bundles) => {
					if (bundles?.length) {
						this.props.change('services', this.resetDeletedServices()); // Remove deleted flag from deleted subservices
						const message = this.generateBundlesErrorMessage(bundles);
						showNotification(message, 'warning');
						closeModal();
					} else {
						// Delete only service groups as BE cascade deletes children booking templates
						return deleteServiceGroups(serviceGroupIds)
							.then(() => {
								showNotification(`${name} - ${t('User_ShopCard_Services_Notification_Deleted')}`, 'success');
								closeModal();
								this.redirectToEditServices();
							})
							.catch((err) => {
								console.error(JSON.stringify(err));
								showNotification(`${name} - ${err}`, 'warning');
							});
					}
				})
				.catch((err) => {
					console.error(JSON.stringify(err));
					showNotification(`${JSON.stringify(err)}`, 'warning');
				});
		} else {
			closeModal();
		}
	};

	private generateBundlesErrorMessage = (bundles: IServicesList[]): string => {
		const { t, servicesToEdit } = this.props;
		const serviceNamesInsideBundle: string[] = [];

		// As the getBundlesForChildren call returns only bundles without children we have to find the children by the customData's children names (not optimal but for this case it should be good enough)
		bundles.forEach((bun) => {
			const bundleServiceNames: string[] = JSON.parse(bun.customData)?.bundleChildrenNames || [];

			bundleServiceNames.forEach((serName) => {
				const tempSerToEdit = servicesToEdit?.find((ser) => ser.name === serName);
				if (tempSerToEdit) {
					serviceNamesInsideBundle.push(serName);
				}
			});
		});

		const uniqueServiceNames: string[] = [...Array.from(new Set(serviceNamesInsideBundle))];
		const message: string =
			t('User_ShopCard_ServiceEdit_PartOfNextBundles1') +
			' ' +
			uniqueServiceNames.join(', ') +
			' ' +
			t('User_ShopCard_ServiceEdit_PartOfNextBundles2') +
			' ' +
			bundles.map((bun) => bun.name).join(', ') +
			t('User_ShopCard_ServiceEdit_PartOfNextBundles3');

		return message;
	};

	private resetDeletedServices = () => {
		const { currentFormValues } = this.props;
		const services = { ...currentFormValues.values.services };

		// Remove deleted flag from all previous existing services
		Object.keys(services).map((ser) => {
			if (services[ser]?.info?.variantParentId) {
				delete services[ser].deleted;
			}
		});

		this.setState((state) => ({ timesResetServiceInBundle: state.timesResetServiceInBundle + 1 }));

		return services;
	};

	private renderBasicInformation = () => {
		const { shopCard, serviceCategories, serviceGroups } = this.props;
		const shopTypes = shopCard?.shopTypes;

		return (
			<BasicInformation
				shopTypes={shopTypes}
				serviceCategories={serviceCategories}
				serviceGroups={serviceGroups}
				currentFormValues={this.props.currentFormValues}
				change={this.props.change}
			/>
		);
	};

	private renderServiceSettings = () => {
		return (
			<ServiceSettings
				officeId={this.props.params.officeId}
				shopCard={this.props.shopCard}
				currentFormValues={this.props.currentFormValues}
				change={this.props.change}
				mode={this.props.route.mode}
				servicesToEdit={this.props.servicesToEdit}
				editModeServiceGroupId={this.props.params.serviceGroupId}
				serviceGroups={this.props.serviceGroups}
				initialize={this.props.initialize}
				isFetching={this.isFetching}
				timesResetServiceInBundle={this.state.timesResetServiceInBundle}
			/>
		);
	};

	private get isFetching(): boolean {
		return (
			this.props.isFetchingBookingTemplates ||
			this.props.isFetchingServiceGroups ||
			this.props.isFetchingServiceCategories ||
			this.props.isFetchingShopCard
		);
	}

	private submitButtonDisabledCheck = (): boolean => {
		if (
			this.isFetching ||
			this.props.submitting ||
			calculateOnScreenServiceVariations(this.props?.currentFormValues?.values?.services) === 0
		) {
			return true;
		} else {
			return false;
		}
	};

	render() {
		const { route, params, handleSubmit, t } = this.props;

		const headerContent = (
			<div className="singleAddEditService__header">
				{route.mode === 'add' ? (
					<>
						<p className="singleAddEditService__header__upperText">{t('User_ShopCard_Services_AddNewService')}</p>
						<div className="singleAddEditService__header__titleContainer">
							{renderIcon('arrow_left', 18, 18, 'singleAddEditService__header__back', () => {
								browserHistory.push(`/shop/${params.shopId}/${params.officeId}/edit/services`);
							})}
							<h1 className="singleAddEditService__header__title">{t('User_ShopCard_Services_NewSingleService')}</h1>
						</div>
					</>
				) : (
					<>
						<p className="singleAddEditService__header__upperText">{t('User_ShopCard_Services_EditExistingService')}</p>
						<div className="singleAddEditService__header__titleContainer">
							{renderIcon('arrow_left', 18, 18, 'singleAddEditService__header__back', () => {
								browserHistory.push(`/shop/${params.shopId}/${params.officeId}/edit/services`);
							})}
							<h1 className="singleAddEditService__header__title">{t('User_ShopCard_Services_EditSingleService')}</h1>
						</div>
					</>
				)}
			</div>
		);

		const bottomContent = (
			<div
				className={`singleAddEditService__bottomContainer ${
					route.mode === 'add' ? 'serviceAddMode' : 'serviceEditMode'
				}`}
			>
				<div className="container">
					<div className="row justify-content-center">
						<div className="col-md-8 col-12">
							<div className="singleAddEditService__bottomContainer__buttonsContainer">
								{route.mode === 'edit' && (
									<Button onClick={() => this.handleOpenDeleteModal()}>
										{renderIcon('delete')} {t('BSPortal_Common_Delete')}
									</Button>
								)}
								<Button type="submit" disabled={this.submitButtonDisabledCheck()}>
									{this.props.submitting ? t('BSPortal_Common_Saving') : t('BSPortal_Common_Save')}
								</Button>
							</div>
						</div>
					</div>
				</div>
			</div>
		);

		return (
			<>
				<LoadingOverlayWrapper
					active={this.isFetching || this.props.submitting}
					className="container singleAddEditContainer"
				>
					<div className="row justify-content-center">
						<div className="col-md-8 col-12">
							{headerContent}
							<form onSubmit={handleSubmit(this.onSubmit)}>
								<div className="profileError">
									<ErrorSummary {...this.props.bookingTemplates} />
								</div>
								{this.renderBasicInformation()}
								{this.renderServiceSettings()}
								{bottomContent}
							</form>
						</div>
					</div>
				</LoadingOverlayWrapper>
			</>
		);
	}
}

function mapStateToProps(state: IStore, _ownProps: IRouterProps) {
	let currentFormValues: IServiceForm = null;
	let servicesToEdit: IServicesList[] = null; // For edit mode (create and fill)
	const { variantParentId, serviceGroupId } = _ownProps.params;
	const route: any = _ownProps.route;

	if (route?.mode === 'edit' && variantParentId && serviceGroupId && state.shopBookingTemplates?.services.length) {
		servicesToEdit = state.shopBookingTemplates.services?.filter(
			(service) => service.serviceGroupId === serviceGroupId,
		);
	}

	if (state.form[c.FORM_NAME_SINGLE_ADD_EDIT_SERVICE] && state.form[c.FORM_NAME_SINGLE_ADD_EDIT_SERVICE].values) {
		currentFormValues = state.form[c.FORM_NAME_SINGLE_ADD_EDIT_SERVICE];
	}

	return {
		state,
		currentFormValues,
		servicesToEdit,
		shopCard: state.shopcard.shopcard,
		serviceCategories: state.domainValues.serviceCategories,
		serviceGroups: state.serviceGroups.serviceGroups,
		bookingTemplates: state.shopBookingTemplates,
		isFetchingBookingTemplates: state.shopBookingTemplates.isFetching,
		isFetchingServiceGroups: state.serviceGroups.isFetching,
		isFetchingServiceCategories: state.domainValues.isFetchingServiceCategories,
		isFetchingShopCard: state.shopcard.isFetching,
	};
}

function mapDispatchToProps(dispatch: Dispatch<IStore>, _ownProps: IRouterProps) {
	return {
		getShopAndServices: (shopId: string, officeId: string, serviceGroupId: string, mode: string) => {
			if (!shopId || !officeId || !serviceGroupId || !mode) {
				return;
			}

			dispatch(resetBookingTemplatesData());
			dispatch(getShopCard(shopId, false, true, false, officeId));

			if (mode === 'edit') {
				dispatch(getBookingTemplatesByServiceGroup(serviceGroupId, true));
			}
		},
		getBookingTemplatesByOffice: (officeId: string, loadResources: boolean) =>
			dispatch(getBookingTemplatesByOffice(officeId, loadResources)),
		getBookingTemplatesByServiceGroup: (serviceGroupId: string, loadResources?: boolean) =>
			dispatch(getBookingTemplatesByServiceGroup(serviceGroupId, loadResources)),
		getServiceCategoriesDomainValues: () => dispatch(getServiceCategoriesDomainValues()),
		getOfficeServiceGroups: (officeId: string) => dispatch(getOfficeServiceGroups(officeId)),
		addServiceGroup: (getState: () => IStore, serviceGroup: IServiceGroup) => addServiceGroup(getState, serviceGroup),
		saveMultipleGroupedBookingTemplate: (getState: () => IStore, shopId: string, variantGroups: IVariantGroups[]) =>
			saveMultipleGroupedBookingTemplate(getState, shopId, variantGroups),
		updatedBatchAddBookingTimeTablesToServices: (
			getState: () => IStore,
			allServices: IServicesList[],
			services: IServicesList[],
			allTimeTablesResourcesTuples: IResourceTimeTables[],
		) => updatedBatchAddBookingTimeTablesToServices(getState, allServices, services, allTimeTablesResourcesTuples),
		fetchBookingTemplateVariants: (getState: () => IStore, variantParentId: string) =>
			fetchBookingTemplateVariants(getState, variantParentId, true, false),
		showNotification: (message: string, kind: 'success' | 'info' | 'warning' | 'danger', dismissAfter = 4000) =>
			dispatch(notifActions.notifSend({ message, kind, dismissAfter })),
		showModal: (type: string, modalProps: any) => dispatch(showModal(type, modalProps)),
		closeModal: () => dispatch(closeModal()),
		deleteServiceGroups: (groupIds: string[]) => dispatch(deleteServiceGroups(groupIds)),
		deleteBookingTemplateVariants: (getState: () => IStore, variantParentId: string) =>
			deleteBookingTemplateVariants(getState, variantParentId),
		removeBookingTemplates: (serviceIds: string[]) =>
			dispatch(removeBookingTemplates(_ownProps.params.officeId, serviceIds)),
		updateServiceGroup: (getState: () => IStore, serviceGroupId: string, serviceGroup: IServiceGroup) =>
			updateServiceGroup(getState, serviceGroupId, serviceGroup),
		getResourceTimeTables: (getState: () => IStore, resourceId: string, dateLimit?: [moment.Moment, moment.Moment]) =>
			getResourceTimetables(getState, resourceId, dateLimit),
	};
}

let ShopServicesSingleAddEdit = reduxForm(
	{
		form: c.FORM_NAME_SINGLE_ADD_EDIT_SERVICE,
		enableReinitizalize: true,
		onSubmitFail: () => scrollErrorIntoView(),
	},
	mapStateToProps,
)(ShopServicesSingleAddEditForm);

ShopServicesSingleAddEdit = connect(mapStateToProps, mapDispatchToProps)(ShopServicesSingleAddEdit);

export default ShopServicesSingleAddEdit;
